import { Instagram, Facebook } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>About BlackBook | BlackBook</title>
        <meta name="description" content="BlackBook — where legacy meets innovation, and community becomes currency. A full-spectrum platform for commerce, culture, community, and empowerment." />
        
        <meta property="og:title" content="About BlackBook | BlackBook" />
        <meta property="og:description" content="Where legacy meets innovation, and community becomes currency. A full-spectrum platform for commerce, culture, community, and empowerment." />
        <meta property="og:image" content="https://www.blackbook.family/logo.jpeg" />
        <meta property="og:url" content="https://www.blackbook.family/about" />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About BlackBook" />
        <meta name="twitter:description" content="Where legacy meets innovation, and community becomes currency." />
        <meta name="twitter:image" content="https://www.blackbook.family/logo.jpeg" />
      </Helmet>
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">About BlackBook</h1>
          
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="text-gray-700 text-lg leading-relaxed mb-8">
              <p className="mb-4">
                Welcome to <strong>BlackBook</strong> — where legacy meets innovation, and community becomes currency.
              </p>
              <p>
                BlackBook is more than a digital directory. It's a dynamic ecosystem built by us, for us, centered around the belief that when Black people support Black enterprise, we all rise. What started as a modern reimagining of the Green Book has grown into a full-spectrum platform for commerce, culture, community, and empowerment.
              </p>
            </div>
            
            <h2 className="text-2xl font-semibold text-blue-900 mb-4">🔍 What We Do Today</h2>
            <ul className="text-gray-700 text-lg leading-relaxed mb-8 space-y-3">
              <li><strong>Directory & Community Portal</strong> — A trusted, GPS-enabled network of FBA‑friendly businesses, from restaurants and hair salons to lodging and services</li>
              <li><strong>BMZ (Black Media Zone)</strong> — Embedded news, commentary, culture, and stories that speak to the heart of Black experience</li>
              <li><strong>BLKVerified</strong> — A badge system affirming businesses that meet ethical, safety, and mission-aligned standards</li>
            </ul>
            
            <h2 className="text-2xl font-semibold text-blue-900 mb-4">🚀 What's Coming (You Don't Want to Miss This)</h2>
            <ul className="text-gray-700 text-lg leading-relaxed mb-8 space-y-3">
              <li><strong>BLKMART</strong> — A curated marketplace for Black-owned products, digital content, and exclusive collections</li>
              <li><strong>Crown & Cattle (Black Subscription Box)</strong> — A premium monthly box featuring meats, spices, wellness goods, or regional delicacies sourced from Black farmers, producers, and artisans</li>
              <li><strong>BLKGPT</strong> — Your AI guide to everything BlackBook: ask about safe travel, find businesses, explore FBA history, or get culture‑aware recommendations</li>
            </ul>
            
            <h2 className="text-2xl font-semibold text-blue-900 mb-4">🎯 Our Mission</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              To build a seamless space where Black people confidently travel, shop, share, and thrive — lifting our communities through connection, visibility, and economic circulation.
            </p>
            
            <h2 className="text-2xl font-semibold text-blue-900 mb-4">🌱 Our Roots & Values</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              BlackBook is rooted in first principles of trust, safety, and reciprocity. We honor the legacy of the original Green Book, while innovating for the modern digital era. Integrity, sovereignty, and excellence guide every feature we build.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              We believe the Black dollar is a compass. When it moves intentionally through our networks, it powers opportunities, builds legacy, and unlocks resilience.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 mt-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-4 text-center">Follow Us on Social Media</h3>
              <p className="text-gray-700 text-center mb-4">Stay connected with The Black Book community for the latest updates, business spotlights, and community events.</p>
              
              <div className="flex justify-center space-x-8">
                <a 
                  href="https://www.instagram.com/theeeblackbook?igsh=NjBkajJkcTlpNTdx&utm_source=qr" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center space-x-2 text-green-700 hover:text-blue-900 transition-colors"
                  data-testid="link-instagram"
                >
                  <Instagram size={32} />
                  <span className="text-lg font-medium">@theeeblackbook</span>
                </a>
                
                <a 
                  href="https://www.facebook.com/profile.php?id=61578640042420" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center space-x-2 text-green-700 hover:text-blue-900 transition-colors"
                  data-testid="link-facebook"
                >
                  <Facebook size={32} />
                  <span className="text-lg font-medium">The Black Book</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
